import {
  lighten,
  transparentize,
} from '@nobia/zeus-components/lib/helpers/polished'
import rem from '@nobia/zeus-components/lib/styled/rem'
import { getTheme } from '@nobia/zeus-components/lib/themes'

const primaryColor = '#192228'
const secondaryColor = '#cc9c74'
const thirdColor = '#fff'
const black = '#192228'
const white = '#fff'

const tone1 = '#f7efe9'
const tone2 = '#faf5f2'
const tone3 = '#F2E8E1'
const tone4 = '#F9F4F0'

const item1 = '#666'
const item2 = '#ccc'
const item3 = '#e7e7e7'
const item4 = '#f2f2f2'

const success = '#73a95e'
const info = '#cc9c74'
const warning = '#cc9c74'
const error = '#7B1D26'
const textColor = '#192228'

const lineColor = '#D8D8D8'

const brickRed = '#7B1D26'
const lighterBlue = '#F2F6FF'
const lightBlue = '#E0E5F1'

const theme = getTheme('magnet')

export default {
  base: {
    fontWeight: 400,
  },
  colors: {
    primary: primaryColor,
    secondary: secondaryColor,
    third: thirdColor,
    black,
    white,
    tone1,
    tone2,
    tone3,
    tone4,
    item1,
    item2,
    item3,
    item4,
    success,
    info,
    warning,
    error,
    darkBlue: primaryColor,
    lighterBlue: lighterBlue,
    lightBlue: lightBlue,
    lightBlueTransparent: '#5f7ed244',
    gold: '#DFC6B3',
    brickRed: brickRed,
    textColor,
    lineColor: '#d7dce4',
  },
  header: {
    wrapper: {
      background: primaryColor,
      color: '#fff',
    },
    title: {
      fontWeight: 400,
      fontSize: 21,
      lineHeight: 21,
    },
    logo: {
      marginLeft: '28px',
      width: theme.rem(79),
      md: {
        width: theme.rem(100),
      },
    },
  },
  close: {
    background: primaryColor,
  },
  home: {
    border: `1px solid #eaeaea`,
    image: {
      borderRadius: rem(6),
    },
  },
  navigation: {
    wrapper: {
      background: '#fff',
      boxShadow: '0 -9px 10px rgba(0, 0, 0, 0.05)',
      border: '1px solid #eee',
      padding: `${theme.rem(4)} 0`,
      lg: {
        padding: `${theme.rem(4)} ${theme.rem(14)}`,
      },
    },
    item: {
      color: primaryColor,
      activeColor: primaryColor,
      activeBackgroundColor: tone1,
      borderRadius: 6,
      fontWeight: 400,
      fontSize: 11,
      lg: {
        fontSize: 12,
      },
    },
  },
  tabs: {
    background: tone1,
    color: primaryColor,
    backgroundColor: tone2,
    activeColor: primaryColor,
    activeBackgroundColor: theme.colors.white,
  },
  buttons: {
    textTransform: 'default',
    borderRadius: 3,
    fontWeight: 500,
    lineHeight: 22,
    padding: {
      small: `${rem(6)}`,
      default: `${theme.rem(12)} ${theme.rem(30)} ${theme.rem(14)}`,
      large: `${theme.rem(21)} ${theme.rem(42)} ${theme.rem(20)}`,
    },
    fontSize: {
      small: 13,
      default: 16,
      large: 16,
    },
    primary: {
      color: white,
      background: primaryColor,
      activeBackground: transparentize(0.15, primaryColor),
    },
    outlined: {
      color: white,
      background: primaryColor,
      activeBackground: transparentize(0.15, primaryColor),
    },
    secondary: {
      color: white,
      background: secondaryColor,
      activeBackground: lighten(0.15, secondaryColor),
    },
    disabled: {
      color: white,
      background: lighten(0.75, primaryColor),
    },
  },
  modal: {
    background: '#fff',
  },
  forms: {
    background: '#fff',
    borderRadius: 3,
    height: rem(50),
    colors: {
      border: '#e9e9ea',
      selected: primaryColor,
    },
  },
  typography: {
    heading1: {
      color: textColor,
      fontFamily: "'NewSpirit', serif",
      fontSize: 30,
      lineHeight: 38,
      fontWeight: 500,
      md: {
        fontSize: 40,
        lineHeight: 44,
      },
    },
    heading2: {
      color: textColor,
      fontFamily: "'NewSpirit', serif",
      fontSize: 23,
      lineHeight: 28,
      fontWeight: 500,
      md: {
        fontSize: 36,
        lineHeight: 40,
      },
    },
    heading3: {
      color: textColor,
      fontFamily: "'NewSpirit', serif",
      fontSize: 21,
      lineHeight: 26,
      fontWeight: 500,
      md: {
        fontSize: 28,
        lineHeight: 35,
      },
    },
    heading4: {
      color: textColor,
      fontFamily: "'NewSpirit', serif",
      fontSize: 16,
      lineHeight: 22,
      fontWeight: 500,
      md: {
        fontSize: 21,
        lineHeight: 26,
      },
    },
    heading5: {
      color: textColor,
      fontFamily: "'NewSpirit', serif",
      fontSize: 16,
      lineHeight: 22,
      fontWeight: 500,
      md: {
        fontSize: 18,
      },
    },
    heading6: {
      color: textColor,
      fontFamily: "'NewSpirit', serif",
      fontSize: 16,
      lineHeight: 22,
      fontWeight: 500,
    },
    preamble: {
      fontFamily: "'NewSpirit', serif",
      fontWeight: 300,
      fontSize: 21,
      lineHeight: 33,
      md: {
        fontSize: 26,
        lineHeight: 40,
      },
    },
    preambleB: {
      fontFamily: "'NewSpirit', serif",
      fontWeight: 300,
      fontSize: 18,
      lineHeight: 28,
      md: {
        fontSize: 21,
        lineHeight: 31,
      },
    },
    paragraphLead: {
      fontWeight: 400,
      fontSize: 18,
      lineHeight: 26,
    },
    paragraph: {
      fontWeight: 400,
      fontSize: 15,
      lineHeight: 24,
      fontFamily: 'Campton, Arial, sans-serif',
      color: primaryColor,
    },
    paragraphSmall: {
      fontWeight: 400,
      fontSize: 13,
      lineHeight: 18,
      md: {
        fontSize: 14,
        lineHeight: 19,
      },
    },
    paragraphByline: {
      fontWeight: 400,
      fontSize: 13,
      lineHeight: 18,
      color: '#7F8487',
      md: {
        fontSize: 14,
        lineHeight: 19,
      },
    },
    link: {
      fontWeight: 400,
      color: primaryColor,
    },
    input: {
      fontSize: 16,
    },
  },
  saved: {
    image: {
      borderRadius: rem(6),
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
      largeProducts: {
        width: '300px',
        height: '180px',
        md: {
          width: '500px',
          height: '300px',
        },
      },
      smallProducts: {
        width: '150px',
        height: '180px',
        md: {
          width: '250px',
          height: '300px',
        },
      },
      kitchenQuizProducts: {
        width: '180px',
        height: '180px',
        md: {
          width: '180px',
          height: '180px',
        },
      },
    },
    sectionHeading: {
      fontFamily: 'NewSpirit',
      fontSize: rem(20),
      fontWeight: 500,
      lineHeight: rem(26),
      md: {
        fontSize: rem(22),
        lineHeight: rem(28),
      },
    },
    product: {
      block: {
        padding: '5px',
      },
      margins: {
        small: rem(25),
        large: rem(25),
      },
      name: {
        color: textColor,
        fontSize: rem(14),
        fontWeight: 500,
        lineHeight: rem(19),
      },
      description: {
        color: '#888',
        fontSize: rem(16),
        fontWeight: 400,
        lineHeight: rem(24),
      },
    },
  },
  appointment: {
    background: 'transparent',
    padding: '0',
    appointment: {
      heading: {
        fontSize: theme.rem(14),
        fontWeight: 500,
        lineHeight: theme.rem(19),
      },
      text: {
        fontSize: theme.rem(14),
        fontWeight: 400,
        lineHeight: theme.rem(19),
      },
      icon: {
        background: tone1,
        color: black,
      },
      hr: {
        color: '#e1d7d0',
      },
    },
    prepare: {
      heading: {
        fontSize: theme.rem(21),
        fontWeight: 500,
        lineHeight: theme.rem(20),
      },
      text: {
        fontSize: theme.rem(14),
        fontWeight: 400,
        lineHeight: theme.rem(19),
      },
    },
  },
  order: {
    card: {
      background: tone3,
      borderRadius: '5px',
    },
    background: white,
    border: `2px solid #cbd0d4`,
    borderRadius: rem(6),
    orderCancelled: {
      errorTextColor: '#e12e21',
    },
    selectItem: {
      paddingBottom: '20px',
      padding: '10px 20px',
      evenColor: white,
      unEvenColor: white,
    },
    label: {
      fontWeight: 400,
    },
  },
  step: {
    uncheckedStep: {
      border: `2px solid ${transparentize(0.5, '#cc9c74')}`,
      background: '#f7efe9',
    },
    boxShadow: `0 11px 27px rgba(151, 133, 133, 0.37)`,
    checkline: {
      width: '2px',
      colors: {
        default: secondaryColor,
        checked: transparentize(0.5, secondaryColor),
      },
      position: {
        height: '30%',
        top: '0',
      },
    },
  },
  overlayBada: {
    icon: {
      background: black,
      color: white,
    },
  },
  checkbox: {
    color: white,
    background: secondaryColor,
    borderColor: secondaryColor,
    uncheckedBackground: secondaryColor,
  },
  loadingIndicator: {
    color: textColor,
  },
  help: {
    topBackground: '#faf5f2',
    background: '#faf5f2',
    borderRadius: rem(2),
    border: `${rem(1)} solid #001c3322`,
    tabs: {
      color: '#7f8487',
      activeColor: textColor,
      indicatorColor: '#cc9c74',
    },
  },
  spacings: {
    xxxs: 2,
    xxs: 4,
    xs: 8,
    s: 12,
    m: 16,
    l: 24,
    xl: 32,
    xxl: 64,
    xxxl: 96,
  },
  carousel: {
    border: `1px solid #fff`,
    borderRadius: rem(6),
    selector: {
      position: 'absolute',
      margin: '10px 10px 0',
    },
  },
  messages: {
    sendMessage: {
      background: lighterBlue,
      borderColor: item4,
      button: {
        background: primaryColor,
        color: white,
      },
    },
    bubble: {
      store: tone1,
      customer: item4,
      initials: {
        background: primaryColor,
      },
    },
    input: {
      borderColor: lineColor,
    },
    textarea: {
      borderColor: lightBlue,
    },
    newMessages: {
      borderColor: item3,
      color: brickRed,
    },
  },
}
